import React, { useEffect, useState, useCallback } from "react";
import {
  Select,
  message,
  Button,
  Modal,
  Input,
  Dropdown,
  Drawer,
  Table,
  Tooltip,
} from "antd";
import { Terminal, Copy, Info, Search } from "lucide-react";
import axios from "axios";

// Import your constants or environment variables
import { AUTH_TOKEN, REACT_APP_API, ORG_ID, DOMAIN_ID } from "../../../constants";
// import { formatTimeByTimezone } from "../../../common/formatTimebyTimezone"; // Not used in current snippet
import MySqlTerminal from "./MySqlTerminal";
import PgAdminConnection from "./pgAdminConnection";
import { AN_COLORS } from "../../../design-system/tokens";
import {
  CountChip,
  EmbeddedShell,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  SquareBadge,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../../design-system/tableUi";

import copyText from "../../../utils/copyText";
const PAGE_SIZE = 8;

const PRIVILEGE_TONE = { READ: "success", WRITE: "warn", EXECUTE: "info" };

// pgAdmin drives a full external client session, so it needs a policy granting
// every privilege. Anything less and the session fails with no explanation.
const PGADMIN_REQUIRED_PRIVILEGES = ["READ", "WRITE", "EXECUTE"];

const hasAllPrivileges = (privileges, required) => {
  if (!Array.isArray(privileges)) return false;
  const granted = privileges.map((p) => String(p).trim().toUpperCase());
  return required.every((r) => granted.includes(r));
};

// `embedded` = rendered inside the Database dashboard's tab card, which already
// supplies the page background and heading, so skip our own shell + header.
const ConnectionsScreen = ({ showNotification, embedded = false }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedHost, setSelectedHost] = useState(null);
  const [selectedPrivilege, setSelectedPrivilege] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const [connectionMode, setConnectionMode] = useState(null);
  // Dropdown filter lists
  const [databaseList, setDatabaseList] = useState([]);
  const [userList, setUserList] = useState([]);

  // Actual connections displayed in the DataGrid
  const [connectionData, setConnectionData] = useState([]);

  // Pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0, // 0-based index
    pageSize: PAGE_SIZE,
  });

  // For user notifications
  const [notification, contextHolder] = message.useMessage();

  // For connection string modal
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);

  // For terminal drawer
  const [isTerminalDrawerOpen, setIsTerminalDrawerOpen] = useState(false);
  const [selectedTerminalConnection, setSelectedTerminalConnection] = useState(null);

  // State for handling loading during connection string refresh attempt
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [currentActionConnectionId, setCurrentActionConnectionId] =
    useState(null);

  const [connectionString, setConnectionString] = useState(null);

  useEffect(() => {
    fetchFilterLists();
  }, []);

  useEffect(() => {
    fetchConnectionData();
  }, [
    selectedDatabase,
    selectedUser,
    selectedHost,
    selectedPrivilege,
    paginationModel,
    searchValue,
  ]);

  const fetchFilterLists = async () => {
    try {
      const payload = { orgId: ORG_ID(), tenantId: DOMAIN_ID() };
      const headers = { "X-Authorization": AUTH_TOKEN(), withCredentials: true };
      const [dbRes, userRes, connectionRes] = await Promise.all([
        axios.post(`${REACT_APP_API}/api/v1/databaseService/getUniqueDatabasenames`, payload, {
          headers,
        }),
        axios.post(`${REACT_APP_API}/api/v1/databaseService/getUniqueUsernames`, payload, {
          headers,
        }),
        axios.post(`${REACT_APP_API}/api/v1/secretservice/getConnectionMode `, payload, {
          headers,
        })
      ]);
      setDatabaseList(dbRes.data?.db_names || []);
      setUserList(userRes.data?.usernames || []);
      setConnectionMode(Number(connectionRes.data?.mode) || [])
    } catch (error) {
      console.error("Error fetching filter lists:", error?.response?.status, error?.message);
      notification.error({ content: "Unable to fetch database/user lists" });
    }
  };

  const fetchConnectionData = () => {
    const filters = [];
    if (selectedHost) filters.push({ filterParameter: "hostname", filterValue: selectedHost });
    if (selectedPrivilege)
      filters.push({ filterParameter: "privilege", filterValue: selectedPrivilege });
    if (selectedUser) filters.push({ filterParameter: "user", filterValue: selectedUser });
    if (selectedDatabase)
      filters.push({ filterParameter: "database_name", filterValue: selectedDatabase.split(" (")[0] });
    // Note: Original code had searchValue filter on "connection_string", adjust if "hostname" or other fields are more appropriate for general search
    if (searchValue) filters.push({ filterParameter: "hostname", filterValue: searchValue });

    const payload = {
      orgId: ORG_ID(),
      tenantId: DOMAIN_ID(),
      filters,
      requestId: "",
      page_id: paginationModel.page + 1,
      limit: paginationModel.pageSize,
    };

    setIsLoading(true);
    const userId = localStorage.getItem("userId");
    const email = localStorage.getItem("UserName");

    axios
      .post(`${REACT_APP_API}/api/v1/databaseService/listConnections`, payload, {
        headers: {
          "X-Authorization": AUTH_TOKEN(),
          userid: userId,
          email: email,
          withCredentials: true,
        },
      })
      .then((res) => {
        const dataArray = res?.data?.data || [];
        const mapped = dataArray.map((item, index) => ({
          ...item,
          id: item.id || `${item.database_name}-${item.ip_address}-${index}`, // Ensure unique ID
        }));
        setConnectionData(mapped);
        setTotalCount(res?.data?.total_count || 0);
      })
      .catch((err) => {
        console.error("Error fetching connections:", err?.response?.status, err?.message);
        notification.error({ content: "Unable to fetch connections" });
      })
      .finally(() => setIsLoading(false));
  };

  const resetPageAndSetState = (setter) => (value) => {
    setter(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Modified to search 'hostname' as per filter construction in fetchConnectionData
  const handleSearch = (value) => {
    setSearchValue(value); // This will be used as 'hostname' filter
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const getMenuItems = (connection, row) => {
    const items = [];

    if (connection === 1 || connection === 3) {
      items.push({
        key: "connect",
        label: "Use Web Console",
        icon: <Terminal size={16} />,
      });
    }

    if (connection === 2 || connection === 3) {
      items.push({
        key: "direct",
        label: "Use Direct Access",
        icon: <Copy size={16} />,
      });
      if (row.dbtype === "postgres") {
        const canUsePgAdmin = hasAllPrivileges(row.user_privilege, PGADMIN_REQUIRED_PRIVILEGES);
        items.push({
          key: "pgAdmin",
          icon: <Copy size={16} />,
          disabled: !canUsePgAdmin,
          label: (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              Use pgAdmin
              {!canUsePgAdmin && (
                <Tooltip
                  classNames={{ body: "!text-xs" }}
                  title="pgAdmin access requires a policy granting all three privileges — READ, WRITE and EXECUTE."
                >
                  {/* antd sets pointer-events: none on disabled menu items, so the
                      tooltip trigger has to opt back in for the icon alone. */}
                  <span
                    style={{ display: "inline-flex", pointerEvents: "auto", cursor: "help" }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Info size={14} />
                  </span>
                </Tooltip>
              )}
            </span>
          ),
        });
      }
    }

    return items;
  };

  const handleMenuClick = async (connection, actionKey) => {
    if (
      actionKey === "pgAdmin" &&
      !hasAllPrivileges(connection.user_privilege, PGADMIN_REQUIRED_PRIVILEGES)
    ) {
      notification.warn({
        content: "pgAdmin requires a policy with READ, WRITE and EXECUTE privileges.",
      });
      return;
    }

    setConnectionString(actionKey);
    setCurrentActionConnectionId(connection.id);
    setIsProcessingAction(true);
    const actionNotificationKey = `freshConn-${connection.id || Date.now()}`; // Unique key for notification
    notification.info({
      content: "Attempting to refresh connection details...",
      key: actionNotificationKey,
      duration: 0,
    });

    let freshConnectionString = connection.connection_string; // Fallback to existing string
    let successfullyRefreshed = false;

    try {
      // Attempt to re-fetch the specific connection using its ID.
      // This assumes the listConnections API supports filtering by 'id'.
      // If not, you'll need to use other available and specific filterParameters.
      const itemSpecificFilters = [{ filterParameter: "id", filterValue: connection.id }];

      // If filtering by "id" is not supported by your API, use other fields:
      // const itemSpecificFilters = [
      //   { filterParameter: "database_name", filterValue: connection.database_name },
      //   { filterParameter: "ip_address", filterValue: connection.ip_address }
      //   // Add other filters if needed to make it unique enough
      // ];

      const payload = {
        orgId: ORG_ID(),
        tenantId: DOMAIN_ID(),
        filters: itemSpecificFilters,
        requestId: "",
        page_id: 1,
        limit: 1, // We expect only one item if filtering by a unique ID
        mode:actionKey === "connect"?"webconsole":actionKey 
      };

      const userId = localStorage.getItem("userId");
      const email = localStorage.getItem("UserName");

      const response = await axios.post(
        `${REACT_APP_API}/api/v1/databaseService/listConnections`,
        payload,
        {
          headers: {
            "X-Authorization": AUTH_TOKEN(),
            userid: userId,
            email: email,
            withCredentials: true,
          },
        },
      );

      if (response.data?.data && response.data.data.length > 0) {
        // Find the item, ideally it's the first and only one
        const refreshedItem = response.data.data.find((item) => item.id === connection.id);
        if (refreshedItem && refreshedItem.connection_string) {
          freshConnectionString = refreshedItem.connection_string;
          successfullyRefreshed = true;
          notification.success({
            content: "Connection details refreshed.",
            key: actionNotificationKey,
            duration: 3,
          });
        } else {
          notification.warn({
            content: "Refresh inconclusive. Using last known string (may be expired).",
            key: actionNotificationKey,
            duration: 5,
          });
        }
      } else {
        notification.warn({
          content:
            "Failed to refresh details (item not found). Using last known string (may be expired).",
          key: actionNotificationKey,
          duration: 5,
        });
      }
    } catch (error) {
      console.error("Error refreshing connection string:", error?.response?.status, error?.message);
      notification.error({
        content: "Error during refresh. Using last known string (may be expired).",
        key: actionNotificationKey,
        duration: 5,
      });
    }

    const updatedConnection = { ...connection, connection_string: freshConnectionString };

    if (actionKey === "connect") {
      setSelectedTerminalConnection(updatedConnection);
      setIsTerminalDrawerOpen(true);
    } else if (actionKey === "pgAdmin" || actionKey === "direct") {
      setSelectedConnection(updatedConnection);
      setIsConnectionModalOpen(true);
    }
    setIsProcessingAction(false);
    setCurrentActionConnectionId(null);
  };

  const handleCopyConnectionString = () => {
    if (selectedConnection?.connection_string) {
      copyText(selectedConnection.connection_string).then(
        () => {
          notification.success({ content: "Connection string copied!" });
          setIsConnectionModalOpen(false);
        },
        () => notification.error({ content: "Couldn't copy. Select the command and copy it manually." }),
      );
    } else {
      notification.error({ content: "No connection string to copy." });
    }
  };

  const handleTerminalExit = useCallback(() => {
    setIsTerminalDrawerOpen(false);
    setSelectedTerminalConnection(null);
    notification.info({ content: "Terminal session closed." });
  }, [notification]);

  const handleDrawerClose = useCallback(() => {
    setIsTerminalDrawerOpen(false);
    setSelectedTerminalConnection(null);
  }, []);

  const columns = [
    {
      title: "Agent host",
      dataIndex: "agent_vm_host_name",
      key: "agent_vm_host_name",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Database",
      dataIndex: "database_name",
      key: "database_name",
      render: (text) => (
        <span style={{ fontWeight: 500, color: AN_COLORS.ink }}>{text || "—"}</span>
      ),
    },
    {
      title: "Database host",
      dataIndex: "dbhost",
      key: "dbhost",
      render: (text) => <MonoCell>{text}</MonoCell>,
    },
    {
      title: "Type",
      dataIndex: "dbtype",
      key: "dbtype",
      width: 120,
      render: (text) => (text ? <SquareBadge>{text}</SquareBadge> : "—"),
    },
    {
      title: "Users",
      dataIndex: "users",
      key: "users",
      render: (value) =>
        Array.isArray(value) && value.length ? (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {value.map((user) => (
              <SquareBadge key={user}>{user}</SquareBadge>
            ))}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "Privileges",
      dataIndex: "user_privilege",
      key: "user_privilege",
      render: (value) =>
        Array.isArray(value) && value.length ? (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {value.map((priv) => (
              <SquareBadge key={priv} tone={PRIVILEGE_TONE[priv]}>
                {priv}
              </SquareBadge>
            ))}
          </span>
        ) : (
          "—"
        ),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      align: "right",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getMenuItems(Number(connectionMode), record),
            onClick: ({ key }) => handleMenuClick(record, key),
          }}
          placement="bottomRight"
          trigger={["click"]}
          disabled={isProcessingAction && currentActionConnectionId === record.id}
        >
          <Button
            type="primary"
            size="small"
            loading={isProcessingAction && currentActionConnectionId === record.id}
          >
            Connect
          </Button>
        </Dropdown>
      ),
    },
  ];

  const appliedFilters = [
    selectedDatabase && {
      key: "Database",
      label: `Database · ${selectedDatabase}`,
      clear: () => resetPageAndSetState(setSelectedDatabase)(null),
    },
    selectedUser && {
      key: "User",
      label: `User · ${selectedUser}`,
      clear: () => resetPageAndSetState(setSelectedUser)(null),
    },
    selectedHost && {
      key: "Host",
      label: `Host · ${selectedHost}`,
      clear: () => resetPageAndSetState(setSelectedHost)(null),
    },
    selectedPrivilege && {
      key: "Privilege",
      label: `Privilege · ${selectedPrivilege}`,
      clear: () => resetPageAndSetState(setSelectedPrivilege)(null),
    },
    searchValue && {
      key: "Search",
      label: `Hostname · ${searchValue}`,
      clear: () => handleSearch(""),
    },
  ].filter(Boolean);

  const clearFilters = () => {
    setSelectedDatabase(null);
    setSelectedUser(null);
    setSelectedHost(null);
    setSelectedPrivilege(null);
    setSearchValue("");
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const Shell = embedded ? EmbeddedShell : PageShell;

  return (
    <>
      {contextHolder}
      <Shell>
        {!embedded && (
          <PageHeader
            eyebrow="Admin · Database · Connections"
            title="Database Connections"
            subtitle="Brokered sessions available to you. Connect through the web console or with a short-lived direct string."
            chips={
              <CountChip
                count={totalCount}
                label={totalCount === 1 ? "Connection" : "Connections"}
              />
            }
          />
        )}

        <FilterCard
          columns="1.6fr 1fr 1fr 1fr"
          applied={appliedFilters}
          onClearAll={clearFilters}
          showing={connectionData.length}
          total={totalCount}
        >
          <FilterField label="Hostname">
            <Input
              allowClear
              placeholder="Search by hostname"
              prefix={<Search size={14} color={AN_COLORS.muted} />}
              style={{ height: 36 }}
              value={searchValue}
              onChange={(e) => handleSearch(e.target.value)}
            />
          </FilterField>

          <FilterField label="Database">
            <Select
              showSearch
              allowClear
              placeholder="Any database"
              style={{ width: "100%", height: 36 }}
              value={selectedDatabase}
              onChange={resetPageAndSetState(setSelectedDatabase)}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={databaseList.map((db) => ({ label: db.split(" (")[0], value: db }))}
            />
          </FilterField>

          <FilterField label="User">
            <Select
              showSearch
              allowClear
              placeholder="Any user"
              style={{ width: "100%", height: 36 }}
              value={selectedUser}
              onChange={resetPageAndSetState(setSelectedUser)}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={userList.map((user) => ({ label: user, value: user }))}
            />
          </FilterField>

          <FilterField label="Privilege">
            <Select
              allowClear
              placeholder="Any privilege"
              style={{ width: "100%", height: 36 }}
              value={selectedPrivilege}
              onChange={resetPageAndSetState(setSelectedPrivilege)}
              options={[
                { label: "READ", value: "READ" },
                { label: "WRITE", value: "WRITE" },
                { label: "EXECUTE", value: "EXECUTE" },
              ]}
            />
          </FilterField>
        </FilterCard>

        <TableCard>
          <Table
            {...anTableProps}
            columns={columns}
            dataSource={connectionData}
            loading={isLoading}
            rowKey="id"
            locale={tableEmpty(
              isLoading,
              appliedFilters.length > 0
                ? "No connections match your filters."
                : "No database connections available to you yet.",
            )}
            pagination={{
              current: paginationModel.page + 1,
              pageSize: paginationModel.pageSize,
              total: totalCount,
              showSizeChanger: false,
              onChange: (page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 })),
            }}
          />
        </TableCard>
      </Shell>

      <Modal
        title="Connection Details"
        open={isConnectionModalOpen}
        onCancel={() => setIsConnectionModalOpen(false)}
        centered
        width={1200}
        className="connection-modal"
        footer={connectionString !== "pgAdmin" &&[
          <Button
            key="copy"
            icon={<Copy size={16} />}
            type="primary"
            onClick={handleCopyConnectionString}
            className="copy-button"
          >
            Copy
          </Button>,
          <Button
            key="close"
            onClick={() => setIsConnectionModalOpen(false)}
            className="close-button"
          >
            Close
          </Button>,
        ]}
      >
        {selectedConnection && (
          <div className="connection-details">
            <h3>Instructions</h3>
            {connectionString === "pgAdmin" ? (
             <PgAdminConnection selectedConnection={selectedConnection}/>
            ) : (
              <>
                <p>
                  Please copy and paste the following command to establish a
                  connection to the database.
                  <br />
                  <strong>Note:</strong> A recent attempt was made to generate
                  this access string. If the connection failed or was
                  inconclusive, the string might be short-lived or expired.
                </p>
                <pre
                  className="connection-string"
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}
                >
                  {selectedConnection.connection_string}
                </pre>
              </>
            )}
          </div>
        )}
      </Modal>

      <Drawer
        title="Web Console"
        placement="bottom"
        onClose={handleDrawerClose}
        open={isTerminalDrawerOpen}
        height="80%"
        destroyOnClose={true}
        rootClassName="terminal-drawer"
      >
        {selectedTerminalConnection && isTerminalDrawerOpen && (
          <MySqlTerminal
            connectionString={selectedTerminalConnection.connection_string}
            onTerminalExit={handleTerminalExit}
            currentConnection={selectedTerminalConnection}
          />
        )}
      </Drawer>

      {/*
        NOTE: `<style jsx>` is Next.js syntax — this is a CRA app, so React just
        renders a plain <style> tag and these rules apply GLOBALLY while this
        screen is mounted. The page/filter/grid rules that used to live here were
        removed with the layout they styled; only the modal + drawer rules below
        are still referenced, by the Connection Details modal and Web Console.
      */}
      <style>{`
        .connection-details {
          padding: 24px;
          background: #f8f9fa;
          border-radius: 8px;
        }
        .connection-details h3 {
          margin-bottom: 16px;
          color: #1a1a1a;
        }
        .connection-details p {
          color: #666;
          margin-bottom: 16px;
        }
        .connection-string {
          background: var(--an-surface);
          padding: 16px;
          border-radius: 6px;
          border: 1px solid var(--an-line);
          overflow-x: auto;
          font-family: "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
          font-size: 13px;
          line-height: 1.5;
        }
        .close-button {
          margin-left: 8px;
        }
        .terminal-drawer .ant-drawer-content-wrapper {
          border-top-left-radius: 8px;
          border-top-right-radius: 8px;
        }
        .terminal-drawer .ant-drawer-body {
          padding: 0;
          height: 100%;
        }
      `}</style>
    </>
  );
};

export default ConnectionsScreen;
